import React, { useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'

// Screens
import Login from './screens/Login'
import AcceptInvite from './screens/AcceptInvite'
import ClaimGarage from './screens/ClaimGarage'
import MembershipSelection from './screens/MembershipSelection'
import Dashboard from './screens/Dashboard'
import Requests from './screens/Requests'
import RequestDetail from './screens/RequestDetail'
import Quotes from './screens/Quotes'
import Calendar from './screens/Calendar'
import Staff from './screens/Staff'
import Profile from './screens/Profile'
import Services from './screens/Services'
import Ads from './screens/Ads'
import Jobs from './screens/Jobs'
import JobDetail from './screens/JobDetail'
import Reviews from './screens/Reviews'
import Layout from './components/Layout'

// Loading screen
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  )
}

// Protected route that requires authentication
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitializing } = useAuthStore()
  
  if (isInitializing) {
    return <LoadingScreen />
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Route for dashboard users (garage staff and car care staff)
function DashboardRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isInitializing, requiresMembershipSelection } = useAuthStore()
  
  if (isInitializing) {
    return <LoadingScreen />
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check if user needs to select a membership
  if (requiresMembershipSelection) {
    return <Navigate to="/select-account" replace />
  }

  return <>{children}</>
}

function App() {
  const initialize = useAuthStore((state) => state.initialize)

  useEffect(() => {
    initialize()
  }, [initialize])

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        {/* Claiming an imported garage from an outreach email. Public: a
            prospect has no account yet, which is the whole point. */}
        <Route path="/claim/:token" element={<ClaimGarage />} />
        {/* Membership selection - requires auth but NOT membership selection */}
        <Route path="/select-account" element={
          <ProtectedRoute>
            <MembershipSelection />
          </ProtectedRoute>
        } />
        <Route
          path="/"
          element={
            <DashboardRoute>
              <Layout />
            </DashboardRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="requests" element={<Requests />} />
          <Route path="requests/:id" element={<RequestDetail />} />
          <Route path="quotes" element={<Quotes />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="staff" element={<Staff />} />
          <Route path="profile" element={<Profile />} />
          <Route path="services" element={<Services />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="jobs/:id" element={<JobDetail />} />
          <Route path="reviews" element={<Reviews />} />
          <Route path="ads" element={<Ads />} />
        </Route>
      </Routes>
    </Router>
  )
}

export default App
